import {Button, Paper, Stack, Text, Title} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useState} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'
import {formatJobDate} from 'src/jobs/job-schedule-format'
import {LoadEntryFields} from 'src/jobs/loads/load-entry-fields'
import {
    emptyLoadEntryForm,
    formatLoadCount,
    type LoadEntryFormValues,
    toLoadEntryInput,
    validateLoadEntry,
} from 'src/jobs/loads/load-entry-logic'

import type {DriverJob, DriverJobLoads} from './schemas'
import {useRecordDriverLoads} from './use-record-driver-loads'

type DriverLoadsCardProps = {
    job: Pick<DriverJob, 'id' | 'startDate' | 'endDate' | 'driverName' | 'driverAssignmentId'>
    loads: DriverJobLoads
    accessToken: string
}

export function DriverLoadsCard({job, loads, accessToken}: DriverLoadsCardProps) {
    const record = useRecordDriverLoads(job.id, accessToken)
    const form = useForm<LoadEntryFormValues>({
        initialValues: emptyLoadEntryForm(job, job.driverAssignmentId),
        validate: (values) => validateLoadEntry(values),
    })
    const [savedMessage, setSavedMessage] = useState<string | null>(null)

    async function handleSave(values: LoadEntryFormValues) {
        setSavedMessage(null)
        await record.mutateAsync(toLoadEntryInput(values))
        setSavedMessage(
            `Saved ${formatLoadCount(values.loadCount ?? 0)} on ${formatJobDate(values.loadDate)}`,
        )
        form.setValues({loadCount: null, ticketNumber: ''})
    }

    return (
        <Paper withBorder p="md" radius="md" data-testid="driver-loads-card">
            <Stack gap="sm">
                <Title order={4}>Loads hauled: {loads.totalLoads}</Title>
                <Text size="sm" c="dimmed">
                    Enter loads per day with the ticket or BOL number. Re-entering a day replaces
                    it.
                </Text>
                {job.driverAssignmentId == null ? null : (
                    <form
                        onSubmit={form.onSubmit(
                            (values) => void handleSave(values).catch(() => {}),
                            () => setSavedMessage(null),
                        )}
                    >
                        <Stack gap="sm">
                            <Text size="sm" fw={500}>
                                Entering loads as {job.driverName}
                            </Text>
                            <LoadEntryFields form={form} />
                            {record.error ? (
                                <Text c="red" size="sm">
                                    {record.error.message}
                                </Text>
                            ) : null}
                            {savedMessage ? (
                                <Text c="green" size="sm">
                                    {savedMessage}
                                </Text>
                            ) : null}
                            <Button type="submit" loading={record.isPending}>
                                Save loads
                            </Button>
                        </Stack>
                    </form>
                )}
                {loads.entries.map((entry) => (
                    <Stack key={entry.id} gap={0}>
                        <Text
                            size="sm"
                            td={entry.voided ? 'line-through' : undefined}
                            c={entry.voided ? 'dimmed' : undefined}
                        >
                            {formatJobDate(entry.loadDate)} · {entry.workerName ?? 'Job total'} ·{' '}
                            {formatLoadCount(entry.loadCount)}
                            {entry.ticketNumber ? ` · Ticket ${entry.ticketNumber}` : ''}
                        </Text>
                        {entry.voided ? (
                            <Text size="xs" c="dimmed">
                                {entry.voidedAt
                                    ? `Voided · ${formatBusinessTimestamp(entry.voidedAt, 'MMM d, h:mm a')}`
                                    : 'Voided'}
                            </Text>
                        ) : null}
                    </Stack>
                ))}
            </Stack>
        </Paper>
    )
}
